<script lang="ts" setup>
import { ref, computed, type PropType } from 'vue'

interface StatusItem {
    pegNumber: number
    vault: number
    section: number
    holdTime: number
    tonsSetPoint: number
    status: string | number
    startTime: string | Date | null
    stopTime: string | Date | null
    totalTime: string | Date | null
}

const props = defineProps({
  statusData: {
    type: Array as PropType<StatusItem[]>,
    required: true,
    default: () => []
  }
});

const formatDate = (date: any) => {
  if (!date) return "--";
  const d = new Date(date);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatTime = (date: any) => {
  if (!date) return "--:--:--";
  const d = new Date(date);
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const getStatusColor = (status: string | number) => {
  const s = status.toString().toLowerCase();
  if (s.includes('complete')) return 'success';
  if (s.includes('waiting')) return 'warning';
  if (s.includes('fail')) return 'error';
  if (s.includes('progress')) return 'info';
  return 'primary';
};

const statusStats = computed(() => {
  const data = props.statusData || [];
  const total = data.length;
  if (total === 0) return { finished: 0, pending: 0, error: 0, finishedPct: 0, pendingPct: 0, errorPct: 0 };

  const finished = data.filter(i => {
    const s = i.status.toString().toLowerCase();
    return s.includes('complete');
  }).length;

  const error = data.filter(i => {
    const s = i.status.toString().toLowerCase();
    return s.includes('fail');
  }).length;

  const pending = total - finished - error;

  return {
    finished,
    pending,
    error,
    finishedPct: (finished / total) * 100,
    pendingPct: (pending / total) * 100,
    errorPct: (error / total) * 100
  };
});

const keyMetrics = computed(() => {
  const data = props.statusData || [];
  if (data.length === 0) return { totalTests: 0, avgLoad: '0.0', avgHoldTime: '0.0' };

  const totalTests = data.length;
  
  const totalLoad = data.reduce((acc, curr) => acc + (curr.tonsSetPoint || 0), 0);
  const avgLoad = (totalLoad / totalTests).toFixed(1);

  const totalHoldTime = data.reduce((acc, curr) => acc + (curr.holdTime || 0), 0);
  const avgHoldTime = (totalHoldTime / totalTests).toFixed(1);

  return {
    totalTests,
    avgLoad,
    avgHoldTime
  };
});

const headers = [
  { title: 'Peg No', key: 'pegNumber', align: 'start' as const },
  { title: 'Location', key: 'location', sortable: false },
  { title: 'Status', key: 'status' },
  { title: 'Load (Tons)', key: 'tonsSetPoint', align: 'end' as const },
  { title: 'Hold Time (s)', key: 'holdTime', align: 'end' as const },
  { title: 'Start Time', key: 'startTime' },
  { title: 'Stop Time', key: 'stopTime' },
  { title: 'Duration', key: 'totalTime' },
];
</script>

<template>
  <v-container fluid>
    <v-alert
      v-if="!statusData || statusData.length === 0"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      No Bolt Status data available.
    </v-alert>

    <div v-else>
      <v-row>
        <v-col cols="12" md="6">
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              Bolt Status
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="d-flex rounded overflow-hidden mb-6" style="height: 24px; background-color: #f1f5f9;">
                 <div class="bg-success transition-swing" :style="{ width: statusStats.finishedPct + '%' }" title="Finished"></div>
                 <div class="bg-error transition-swing" :style="{ width: statusStats.errorPct + '%' }" title="Error"></div>
                 <div class="bg-warning transition-swing" :style="{ width: statusStats.pendingPct + '%' }" title="Pending"></div>
              </div>
              <div class="d-flex justify-space-between px-2">
                 <div class="d-flex align-center">
                   <v-badge dot color="success" inline class="mr-2"></v-badge>
                   <span class="text-body-2 text-medium-emphasis">Finished: <strong class="text-high-emphasis">{{ statusStats.finished }}</strong></span>
                 </div>
                 <div class="d-flex align-center">
                   <v-badge dot color="error" inline class="mr-2"></v-badge>
                   <span class="text-body-2 text-medium-emphasis">Error: <strong class="text-high-emphasis">{{ statusStats.error }}</strong></span>
                 </div>
                 <div class="d-flex align-center">
                   <v-badge dot color="warning" inline class="mr-2"></v-badge>
                   <span class="text-body-2 text-medium-emphasis">Pending: <strong class="text-high-emphasis">{{ statusStats.pending }}</strong></span>
                 </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              Key Metrics
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="d-flex justify-space-around align-center py-6">
               <div class="text-center">
                 <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ keyMetrics.totalTests }}</div>
                 <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Total Tests</div>
               </div>
               <v-divider vertical class="my-2"></v-divider>
               <div class="text-center">
                 <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ keyMetrics.avgLoad }}</div>
                 <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Avg Load (Tons)</div>
               </div>
               <v-divider vertical class="my-2"></v-divider>
               <div class="text-center">
                 <div class="text-h4 font-weight-bold text-blue-grey-darken-3">{{ keyMetrics.avgHoldTime }}</div>
                 <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mt-1">Avg Hold Time (s)</div>
               </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="mt-6" elevation="2">
        <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
          <span class="text-subtitle-1 font-weight-bold">Bolt Status Details</span>
        </v-card-title>
        <v-divider></v-divider>
        
        <v-data-table
          :headers="headers"
          :items="statusData"
          density="compact"
          class="text-body-2"
          hover
        >
          <template v-slot:item.pegNumber="{ item }">
            <v-chip size="small" variant="flat" color="grey-lighten-3" class="font-weight-bold text-body-2">
              {{ item.pegNumber }}
            </v-chip>
          </template>

          <template v-slot:item.location="{ item }">
            <div class="d-flex gap-2">
              <div class="d-flex flex-column align-center border rounded px-2 py-1 bg-grey-lighten-5 mr-2" style="min-width: 50px;">
                <span class="text-caption text-medium-emphasis font-weight-bold" style="font-size: 0.65rem;">VAULT</span>
                <span class="font-weight-bold text-body-2">{{ item.vault }}</span>
              </div>
              <div class="d-flex flex-column align-center border rounded px-2 py-1 bg-grey-lighten-5" style="min-width: 50px;">
                <span class="text-caption text-medium-emphasis font-weight-bold" style="font-size: 0.65rem;">SECTION</span>
                <span class="font-weight-bold text-body-2">{{ item.section }}</span>
              </div>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
              class="text-uppercase font-weight-bold"
              variant="tonal"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:item.startTime="{ item }">
            <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.startTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.startTime) }}</span>
            </div>
          </template>

          <template v-slot:item.stopTime="{ item }">
            <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.stopTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.stopTime) }}</span>
            </div>
          </template>
          
          <template v-slot:item.totalTime="{ item }">
             <div class="d-flex align-center font-weight-bold">
                <v-icon size="small" class="mr-1 text-medium-emphasis">mdi-timer-outline</v-icon>
                {{ item.totalTime }}
             </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-container>
</template>

<style scoped>
.transition-swing {
  transition: width 0.5s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>